import {LitElement, css, html, PropertyValues} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { ParameterValue, SimulatedParamData } from '../../../src/models/voyage-performance-model';
import { PerformanceUtils } from '../../../src/utils/performance-utils';
import "../../voy-timeseries-chart/src/voy-timeseries-chart";
import { PilotPointTimestamps, TimeseriesData } from '../../voy-timeseries-chart/src/echartUtils';
import { VoyTimeseriesChart } from '../../voy-timeseries-chart/src/voy-timeseries-chart';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';

@customElement('voyage-performance-chart')
export class VoyagePerformanceChart extends LitElement {

    @property({ type: String }) performanceChartTitle : string = "";
    @property({ type: String }) unit: string = "";
    @property({ type: Array }) simulated: SimulatedParamData[] = [];
    @property({ type: Array }) actual: ParameterValue[] = [];
    @property({ type: String }) url: string = "";
    @property({ type: String }) tooltip: string = "";
    @property({ type: Boolean }) hasSC: boolean = false;
    @property({ type: Boolean }) showSpinner: boolean = false;
    @property({attribute: false}) pilotPoints: PilotPointTimestamps|null = null;
    @property({ type: Boolean }) private open: boolean = false;

    get chart() {
        return this._chart ? this._chart : null;
    }

    static get styles() {
        return [
            css`
            :host {
                display: flex;
                flex-direction: column;
                box-sizing: border-box;
                border-radius: 3px;
                border-bottom: 1px solid var(--neutrals-grey-07, grey);
                font-family: 'MaerskText';
                width: 100%;
                height: 100%;
            }

            .header {
                display: flex;
                margin: 0;
                padding: 16px;
            }

            .title {
                color: var(--neutrals-grey-03, black);
                font-size: 14px;
                line-height: 16px;
            }

            .hassc {
                cursor: pointer;
            }

            span {
                flex-grow: 1;
            }

            .accordion {
                text-align: end;
            }

            .accordion svg {
                height: 16px;
                width: 16px;
            }
        `];
    }

    render() {
        return html`
            <div class="header ${this.hasSC ? 'hassc' : ''}" title=${this.hasSC ? this.tooltip : this.performanceChartTitle}>
              <span class="title">${this.performanceChartTitle}</span>
              ${!this.open ?  html`<span class="accordion" @click = ${()=>this.open=true}>${SvgIcons.chevronDown}</span>` : html`<span class="accordion" @click = ${()=>this.open=false}>${SvgIcons.chevronUp}</span>` }
            </div>
            <div class="info"></div>
            ${this.open ? html`${this.pilotPoints ? 
                html`<voy-timeseries-chart id="plot" show-legend unit=${this.unit} .data=${this._chartData} .showSpinner=${this.showSpinner} .pilotPoints=${this.pilotPoints}></voy-timeseries-chart>` : 
                html`<voy-timeseries-chart id="plot" show-legend unit=${this.unit} .data=${this._chartData} .showSpinner=${this.showSpinner}></voy-timeseries-chart>`
                }` : html``}
            ` 
    }
        
    updated(changedProperties: PropertyValues)
    {
        if (changedProperties.has("actual") || changedProperties.has("simulated"))
        {
            this._setData();
        }
    }

    firstUpdated()
    {
        this._chart = this.shadowRoot!.querySelector('#plot');
    }

    clear()
    {
        this.actual = [];
        this.simulated = [];
    }

    @property({attribute: false}) private _chartData: TimeseriesData[] = [];
    private _chart: VoyTimeseriesChart|null = null;

    private _setData()
    {
        this._chartData = [];

        if (this.actual.length > 0)
        {
            this._chartData.push({
                name: "Actual",
                color: "--performance-viewer-actual-color",
                timeseries: PerformanceUtils.paramValuesToTimeseries(this.actual),
                fillArea: true,
                animate: true,
                tooltip: "Vessel trail based on position data"
            });
        }

        for (const simulatedData of this.simulated)
        {
            let planName = "";
            switch (simulatedData.source) {
                case "EcoVoyage": { planName = "EcoVoyage"; break }
                case "Voyage": { planName = "Voyage Planning"; break }
                case "Excel": { planName = "Excel"; break }
            }
            this._chartData.push({
                name: "Planned",
                color: "--voyage-performance-chart-simulated-color",
                timeseries:  PerformanceUtils.paramValuesToTimeseries(simulatedData.data),
                fillArea: true,
                animate: true,
                tooltip: "Latest Voyage plan by vessel"
            });
        }
    }

    private _onClick() {
        window.open(this.url, "_blank");
    }
    
};
